import { Box, Container, Flex, HStack, Heading, Tag, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ApiError } from '../../../apiClient';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { usePublicHelpArticle, usePublicHelpArticles } from '../hooks/useHelpArticles';
import { usePublicHelpCategories } from '../hooks/useHelpCategories';
import HelpSidebar from './HelpSidebar';

export default function HelpArticlePage() {
  const { categorySlug, articleSlug } = useParams<{ categorySlug: string; articleSlug: string }>();
  const [lang, setLang] = useState<'fr' | 'en'>('fr');
  const navigate = useNavigate();

  /**
   * The one read on this page whose failure was reported to the reader as a
   * FACT: `article === undefined` with nothing in flight rendered *"Article non
   * trouvé."*, and that was the branch for a 502, a dropped connection and a
   * genuinely deleted article alike.
   *
   * The distinction is available and is not a guess. `GET help/articles/:slug`
   * throws `NotFoundException` for an unknown slug and for a DRAFT article
   * (`api/src/help-center/service/`, and its spec pins both), and every other
   * failure arrives with its own status — so a 404 is the only one that means
   * what the old sentence said. `ApiError` carries `status`; the `instanceof`
   * form is the house idiom (`ChangeUserPasswordModal`, `useCancelAdminBooking`).
   */
  const { data: article, isLoading, isError, error } = usePublicHelpArticle(articleSlug || '', lang);
  /**
   * Both derived flags require the DATA to be absent as well as the flag to be
   * raised: a failed background refetch leaves the article on screen and
   * correct, and neither "not found" nor "could not load" is true of it.
   */
  const articleUnreadable = isError && !article;
  const articleMissing = articleUnreadable && error instanceof ApiError && error.status === 404;

  /**
   * The navigation reads. Their failure is not a lie — the sidebar simply does
   * not render, because it is gated on both being present — but it is silent,
   * and silence on a page whose own article failed to load leaves the reader
   * with no way out except the browser's back button. They get one notice
   * between them: this is wayfinding, not content, and two alerts about two
   * halves of the same outage is noise.
   */
  const { data: categories, isError: categoriesError } = usePublicHelpCategories(lang);
  const { data: articles, isError: articlesError } = usePublicHelpArticles(lang);
  const navigationError = (categoriesError && !categories) || (articlesError && !articles);

  // Content is sanitized server-side (sanitize-html) AND client-side (DOMPurify) for defense-in-depth
  const sanitizedContent = article ? DOMPurify.sanitize(article.content) : '';

  return (
    <Container maxW="1100px" py={12}>
      {/* Language Toggle */}
      <HStack justify="flex-end" mb={6}>
        <Tag size="md" variant={lang === 'fr' ? 'solid' : 'outline'} colorScheme="brand" cursor="pointer" onClick={() => setLang('fr')}>FR</Tag>
        <Tag size="md" variant={lang === 'en' ? 'solid' : 'outline'} colorScheme="brand" cursor="pointer" onClick={() => setLang('en')}>EN</Tag>
      </HStack>

      {/* Breadcrumb */}
      <HStack mb={6} fontSize="sm" color="gray.500">
        <Text cursor="pointer" _hover={{ color: 'brand.500' }} onClick={() => navigate('/marketing/help')}>
          {lang === 'fr' ? 'Centre d\'aide' : 'Help Center'}
        </Text>
        <Text>/</Text>
        <Text cursor="pointer" _hover={{ color: 'brand.500' }} onClick={() => navigate(`/marketing/help/${categorySlug}`)}>
          {categories?.find((c) => c.slug === categorySlug)?.[lang === 'en' ? 'nameEn' : 'nameFr'] || '...'}
        </Text>
        <Text>/</Text>
        <Text fontWeight="medium" color="gray.700">{article?.title || '...'}</Text>
      </HStack>

      <Flex gap={8} direction={{ base: 'column', md: 'row' }}>
        {/* Sidebar */}
        {categories && articles ? (
          <Box display={{ base: 'none', md: 'block' }}>
            <HelpSidebar
              categories={categories}
              articles={articles}
              currentSlug={articleSlug}
              lang={lang}
            />
          </Box>
        ) : navigationError ? (
          <Box display={{ base: 'none', md: 'block' }} maxW="240px">
            <InlineErrorState
              message={
                lang === 'fr'
                  ? 'Navigation indisponible pour le moment.'
                  : 'Navigation is unavailable right now.'
              }
            />
          </Box>
        ) : null}

        {/* Article Content — sanitized by DOMPurify (client) + sanitize-html (server) */}
        <Box flex="1" minW={0}>
          {isLoading ? (
            <Text color="gray.500">Loading...</Text>
          ) : article ? (
            <>
              <Heading size="lg" mb={6}>{article.title}</Heading>
              <Box
                className="help-content"
                sx={{
                  'h1': { fontSize: '2xl', fontWeight: 'bold', mt: 8, mb: 4 },
                  'h2': { fontSize: 'xl', fontWeight: 'semibold', mt: 6, mb: 3 },
                  'h3': { fontSize: 'lg', fontWeight: 'semibold', mt: 4, mb: 2 },
                  'p': { mb: 4, lineHeight: 1.7 },
                  'img': { borderRadius: 'lg', my: 4, maxW: '100%' },
                  'ul, ol': { pl: 6, mb: 4 },
                  'li': { mb: 1 },
                  'a': { color: 'blue.500', textDecoration: 'underline' },
                  'code': { bg: 'gray.100', px: 1, borderRadius: 'sm', fontSize: 'sm' },
                  'pre': { bg: 'gray.50', p: 4, borderRadius: 'md', overflowX: 'auto', mb: 4 },
                  'blockquote': { borderLeft: '3px solid', borderColor: 'gray.300', pl: 4, my: 4, color: 'gray.600' },
                  'hr': { my: 6, borderColor: 'gray.200' },
                  'table': { w: 'full', mb: 4 },
                  'th, td': { border: '1px solid', borderColor: 'gray.200', p: 2 },
                }}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              />
            </>
          ) : articleMissing ? (
            <Text color="gray.500">{lang === 'fr' ? 'Article non trouv\u00e9.' : 'Article not found.'}</Text>
          ) : (
            <InlineErrorState
              message={
                lang === 'fr'
                  ? 'Impossible de charger cet article pour le moment. Veuillez r\u00e9essayer.'
                  : 'We could not load this article right now. Please try again.'
              }
            />
          )}
        </Box>
      </Flex>
    </Container>
  );
}
